import React, { useEffect, useState } from "react";
import StudentForm, {
  CustomField,
  StudentFormSchema,
} from "../../../StudentPages/components/StudentForm";
import { ADD_STUDENT } from "../../../../features/graphql/mutation/students/addStudent";
import { useMutation, useQuery, gql } from "@apollo/client";
import {
  ApiValidationError,
  FormSubmitCallback,
  mapApiFieldValidationErrors,
} from "../../../../components/Form";
import { useSlideInContext } from "../../../../context/SlideInContext";
import {
  AddStudentMutation,
  AddStudentMutationVariables,
  CustomFieldInput,
  LocationsQuery,
  LocationsQueryVariables,
} from "../../../../__generated__/graphql";
import { STUDENTS_PAGE_QUERY } from "../../../StudentPages/components/StudentTable";
import usePersonStore from "../../../store/personStore";
import { useManageModal } from "../../../Manage/components/ManageModal";

const LOCATION_QUERY = gql(`
  query Locations {
    locations {
      __typename
      id
      customFields {
        __typename
        id
        type
        name
      }
    }
  }
`);

export default function AddStudent() {
  const [addStudent, { error }] = useMutation<AddStudentMutation, AddStudentMutationVariables>(ADD_STUDENT, {
    refetchQueries: [STUDENTS_PAGE_QUERY],
  });

  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const { toggleDrawer } = useSlideInContext();
  const { loading, data } = useQuery<LocationsQuery, LocationsQueryVariables>(LOCATION_QUERY);
  const defaultLocation = usePersonStore((state) => state.company.defaultLocation);
  const { openModal } = useManageModal()
  useEffect(() => {
    const _location = data?.locations.find((location) => location.id === defaultLocation?.id)
    const customFields: CustomField[] = _location?.customFields.map((field): CustomField => ({
      id: field.id,
      name: field.name ?? "",
      type: field.type ?? ""
    })) ?? [];

    setCustomFields(customFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  const handleAddStudentSubmission: FormSubmitCallback<StudentFormSchema> = async (
    values
  ): Promise<ReturnType<FormSubmitCallback>> => {
    const customFields = Object.keys(values.customFields ?? {})
      .filter((key) => values?.customFields?.[key] ?? null)
      .map((key) => {
        // Shouldn't be null undefined and nulls are filtered out
        const customField = values.customFields![key];
        if (!customField.id || !customField.value) {
          return undefined
        }
        return {
          id: customField.id,
          value: customField.value,
        } as CustomFieldInput;
      }).filter((field): field is CustomFieldInput => field !== undefined);
    const input = {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dob: values.dob ? new Date(values.dob).toISOString() : null,
      postalCode: values.postalCode,
      parentEmail: values.parentEmail,
      parentPhone: values.parentPhone,
      email: values.email,
      customFields: customFields ?? [],
    };
    try {

      const response = await addStudent({ variables: { input } });
      if (response.data?.addStudent.errors) {
        const validationError = response.data.addStudent.errors.find(
          e => e.__typename === "ValidationError"
        ) as ApiValidationError;

        const fieldErrors = validationError
          ? mapApiFieldValidationErrors(validationError)
          : null;
        return fieldErrors;
      }

      else {
        toggleDrawer();
      }
    } catch (e) {
      openModal({ type: "GENERAL_ERROR" })
    }
  };

  return (
    <StudentForm
      onSubmit={handleAddStudentSubmission}
      customFields={customFields}
      initialValues={null}
      onCancel={() => toggleDrawer()}
    />
  );
}
